import { useState, type FormEvent } from "react";
import { request } from "@/lib/api/client";
import { ApiError, NetworkError } from "@/lib/api/errors";
import { saveSession } from "@/lib/auth/session";
import { Icon } from "@/components/ui/Icon";
import { isMockMode, setMockMode } from "@/lib/api/mock/mode";
import "./login.css";
import brandLogo from "@/assets/brand/uyda-logo.svg";

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_at: string;
  user: {
    id: string;
    username: string;
    full_name: string;
    roles: string[];
    permissions: string[];
    warehouses: string[];
  };
}

export function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mock, setMock] = useState(isMockMode);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const token = await request<TokenResponse>("identity", "/api/v1/auth/login", {
        method: "POST",
        body: { username: username.trim(), password },
      });

      saveSession(token.access_token, {
        id: token.user.id,
        username: token.user.username,
        fullName: token.user.full_name,
        roles: token.user.roles ?? [],
        // Vakolatlar SERVERDAN keladi (dev_spec.md §11.2) —
        // interfeys ularni o'zi hisoblamaydi.
        permissions: token.user.permissions ?? [],
        warehouses: token.user.warehouses ?? [],
      }, token.refresh_token);
    } catch (cause) {
      if (cause instanceof ApiError) {
        setError(cause.body.message);
      } else if (cause instanceof NetworkError) {
        setError("identity-service javob bermadi. Servis ishga tushganini tekshiring.");
      } else {
        setError(String(cause));
      }
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card glass-thick" onSubmit={submit}>
        <div className="login-brand">
          <img className="login-logo" src={brandLogo} alt="Uyda" />
          <div>
            <div className="login-title">WMS</div>
            <div className="login-sub">Administrator paneli</div>
          </div>
        </div>

        <label className="field">
          <span className="field-label">Foydalanuvchi</span>
          <input
            className="input"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span className="field-label">Parol</span>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {error && (
          <div className="login-error" role="alert">
            <Icon name="alert" size={16} />
            <span>{error}</span>
          </div>
        )}

        <button type="submit" className="btn btn-filled login-submit" disabled={busy}>
          {busy ? "Tekshirilmoqda…" : "Kirish"}
        </button>

        <p className="login-note">
          {mock
            ? "Namuna rejimi: istalgan nom va parol bilan kiring."
            : "Vakolatlar serverdan keladi: qaysi modullar ko'rinishi rolingizga bog'liq."}
        </p>

        {/* Namuna rejimi KIRISHDAN OLDIN almashtiriladi:
            serversiz muhitda uni sozlamalardan yoqish uchun
            avval tizimga kirish kerak bo'lardi — kirish esa
            aynan serverni talab qiladi. */}
        <label className="login-mock">
          <input
            type="checkbox"
            checked={mock}
            onChange={(event) => {
              setMockMode(event.target.checked);
              setMock(event.target.checked);
              setError(null);
            }}
          />
          <span>
            <b>Namuna ma'lumot bilan ochish</b>
            <br />
            Serverga ulanmasdan butun panelni ko'rish uchun
          </span>
        </label>
      </form>
    </div>
  );
}
